import { FC, useContext, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useCarDocumentsApi } from "../../http/carDocuments/api";
import { CarDocument, RenameDocument, ShowCarDocumentToUser } from "../../http/carDocuments/types";
import { isConflict } from "../../http/errors";
import { PaginationQuery } from "../../types";
import { formatTimeStamp, isEmbed, isImage, isMobile, isVideo } from "../../types/utils";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgEdit from "../icons/Edit";
import SvgVisible from "../icons/Visibility";
import SvgNotVisible from "../icons/VisibilityOff";
import { Check } from "../ui/Check";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { Action, getDeleteAction, getDownloadAction, getPreviewAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import DeleteCarDocumentDialog from "./DeleteCarDocumentDialog";
import UploadCarDocument from "./UploadCarDocument";

type CarDocumentsProps = {
  setIsDragging: (isDragging: boolean) => void;
};

const CarDocuments: FC<CarDocumentsProps> = ({ setIsDragging }) => {
  const i18n = useI18n();
  const carDocumentsApi = useCarDocumentsApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [itemToDelete, setItemToDelete] = useState<CarDocument>();
  const [documentIdToRename, setDocumentIdToRename] = useState<string>("");
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const { id: carId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const docId = searchParams.get("doc_id");
  const api = useApi();
  const canUploadDocuments = !!carId && userSettings.permissions.cars.insert;

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = carDocumentsApi.useCarDocuments(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const uploadCarDocument = async (file: File): Promise<TableUploadFileResult> => {
    if (!carId) {
      return "skipped";
    }

    try {
      await carDocumentsApi.insertCarDocument(file, false, carId);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            carDocumentsApi
              .insertCarDocument(file, true, carId)
              .then(() => {
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const onSubmit = async (id: string, name: string, callback: () => void): Promise<void> => {
    const body: RenameDocument = {
      name: name,
    };

    try {
      await carDocumentsApi.renameCarDocument(id, body, carId);
      callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const downloadDocument = (item: CarDocument) =>
    api
      .downloadFile(`/api/admin/car_documents/${item.id}` + `?t=${new Date().getTime()}`, item.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const onPreviewClick = async (item: CarDocument) => {
    if (!isMobile && (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))) {
      const docUrl = await api.createObjectURL(
        `/api/admin/car_documents/${item.id}` + `?t=${new Date().getTime()}`,
        true,
      );
      setItemToPreview({ name: item.name, url: docUrl });
      return;
    }
    downloadDocument(item);
  };

  const getRowActions = (item: CarDocument) => {
    const actions: Action[] = [];

    if (userSettings.permissions.cars.update) {
      actions.push({
        title: item.isVisibleToUser ? i18n.t.common.hideToUser : i18n.t.common.showToUser,
        onClick: async () => {
          const body: ShowCarDocumentToUser = { isVisibleToUser: !item.isVisibleToUser };
          await carDocumentsApi
            .updateCarDocumentVisibleToUser(item.id, body, item.car.id)
            .catch((err) => dispatch(showErrorNotification(i18n, err)));
        },
        icon: item.isVisibleToUser ? SvgNotVisible : SvgVisible,
      });
    }

    if (userSettings.permissions.cars.read) {
      actions.push(getDownloadAction(i18n, () => downloadDocument(item)));
    }

    if (
      userSettings.permissions.cars.read &&
      !isMobile &&
      (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))
    ) {
      actions.push(getPreviewAction(i18n, () => onPreviewClick(item)));
    }

    if (userSettings.permissions.cars.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setDocumentIdToRename(item.id),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.cars.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Table<CarDocument>
        filters={filters}
        fileDrop={{
          enabled: canUploadDocuments,
          onUploadFile: uploadCarDocument,
          isDraggingOn: setIsDragging,
        }}
        buttons={{
          main: !!carId && userSettings.permissions.cars.insert && <UploadCarDocument carId={carId} />,
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.uploadedBy}</th>
            <th align="left">{i18n.t.common.uploadedAt}</th>
            <th align="left">{i18n.t.common.modifiedBy}</th>
            <th align="left">{i18n.t.common.modifiedAt}</th>
            <th align="left">{i18n.t.common.visibleToUser}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <Table.EditableCell
              defaultName={item.name}
              itemId={item.id}
              onSubmit={onSubmit}
              onCancel={() => setDocumentIdToRename("")}
              isEditing={item.id === documentIdToRename}
              disabled={!userSettings.permissions.cars.update}
            >
              <DownloadLink url={`/api/admin/car_documents/${item.id}`} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </Table.EditableCell>
            <td>{item.uploader.fullName}</td>
            <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
            <td>{item.editor?.fullName ?? ""}</td>
            <td>{!!item.editor ? formatTimeStamp(item.modifiedAt, i18n.language) : ""}</td>
            <td align="center">
              <Check
                icon={item.isVisibleToUser ? SvgCheckCircle : SvgCancel}
                color={item.isVisibleToUser ? "green" : "red"}
                obscured={!item.isVisibleToUser}
              />
            </td>
          </>
        )}
        getRowStyle={(item) => (docId && item.id == docId ? ({ "--row-bg": "#f9ffbc", "--row-hover-bg": "#ecf5a0" } as React.CSSProperties) : undefined)}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <DeleteCarDocumentDialog carDocument={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default CarDocuments;
